/**
 * Small date helpers for the board and the activity feed. All inputs are the
 * API's ISO-8601 UTC strings; `now` is injectable for tests.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole days since `iso` (0 for today). Null for unparseable input. */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  const d = parseIso(iso);
  if (!d) return null;
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / DAY));
}

/** Short "time in stage" label for a card: `today`, `1d`, `12d`. */
export function daysLabel(iso: string | null | undefined, now: Date = new Date()): string {
  const days = daysSince(iso, now);
  if (days === null) return '';
  if (days === 0) return 'today';
  return `${days}d`;
}

/** Relative time for the activity feed: `just now`, `5m ago`, `3h ago`, `2d ago`, `Jan 4`. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  const d = parseIso(iso);
  if (!d) return '';
  const diff = now.getTime() - d.getTime();
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < 14 * DAY) return `${Math.floor(diff / DAY)}d ago`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** Full timestamp for tooltips / `<time title>`. */
export function fullDateTime(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
